<template>
  <div class="mt-4 px-4 container-fluid">
    <div class="breadcrumb text-md px-0 mb-4">
      <p>
        <span class="breadcrumb-item" @click="$router.push('/dashboard')">Inicio</span>
        &rsaquo; <span class="breadcrumb-item" @click="$router.push('/informes')">Informes</span>
        &rsaquo; Comisiones
      </p>
    </div>
  </div>

  <div class="px-4 container-fluid">
    <div class="card mt-2">
      <div class="card-header pb-2">
        <h5 class="mb-1">Informe de Comisiones</h5>
      </div>

      <div class="card-body pt-3">

        <!-- Selector de tipo de período -->
        <div class="periodo-tabs mb-3">
          <button
            v-for="p in periodos" :key="p.v"
            :class="['periodo-tab', { active: periodo === p.v }]"
            @click="periodo = p.v"
          >{{ p.l }}</button>
        </div>

        <!-- Filtros -->
        <div class="filtros-grid mb-4">
          <div class="filtro-grupo">
            <label class="filtro-label">Tipster</label>
            <select v-model="tipsterId" class="filtro-input">
              <option value="">Todos</option>
              <option v-for="t in listaTipsters" :key="t.id" :value="t.id">{{ t.nombre }}</option>
            </select>
          </div>

          <div class="filtro-grupo" v-if="periodo === 'mes'">
            <label class="filtro-label">Mes</label>
            <select v-model="mes" class="filtro-input">
              <option v-for="m in meses" :key="m.v" :value="m.v">{{ m.l }}</option>
            </select>
          </div>

          <div class="filtro-grupo" v-if="periodo === 'trimestre'">
            <label class="filtro-label">Trimestre</label>
            <select v-model="trimestre" class="filtro-input">
              <option value="1">T1 (Ene - Mar)</option>
              <option value="2">T2 (Abr - Jun)</option>
              <option value="3">T3 (Jul - Sep)</option>
              <option value="4">T4 (Oct - Dic)</option>
            </select>
          </div>

          <div class="filtro-grupo" v-if="periodo === 'semestre'">
            <label class="filtro-label">Semestre</label>
            <select v-model="semestre" class="filtro-input">
              <option value="1">S1 (Ene - Jun)</option>
              <option value="2">S2 (Jul - Dic)</option>
            </select>
          </div>

          <div class="filtro-grupo">
            <label class="filtro-label">Año</label>
            <select v-model="anio" class="filtro-input">
              <option v-for="a in anios" :key="a" :value="a">{{ a }}</option>
            </select>
          </div>
        </div>

        <!-- Botones de acción -->
        <div class="acciones-row mb-4">
          <QBtn variant="tipster"  :label="loading      ? 'Cargando...'   : 'Generar informe'" :loading="loading"      :disabled="loading                  " @click="cargar"     />
          <QBtn variant="neutro"   :label="exportando   ? 'Exportando...' : 'Exportar Excel'"  :loading="exportando"   :disabled="!datos.length || exportando" @click="exportar"    />
          <QBtn variant="cancela"  :label="generandoPdf ? 'Generando...'  : 'Generar PDF'"     :loading="generandoPdf" :disabled="!datos.length || generandoPdf" @click="generarPdf" />
        </div>

        <!-- Sin datos todavía -->
        <div v-if="!generado" class="text-center py-5 text-muted">
          <p class="mb-0">Selecciona los filtros y pulsa <strong>Generar informe</strong>.</p>
        </div>

        <div v-else-if="!datos.length" class="text-center py-5 text-muted">
          <p class="mb-0">No hay ventas confirmadas en este período.</p>
        </div>

        <!-- Tabla de resultados -->
        <div v-else>
          <p class="text-sm text-muted mb-3 periodo-label-txt">{{ periodoLabel }}</p>

          <div class="table-responsive">
            <table class="table align-items-center mb-0">
              <thead>
                <tr>
                  <th class="text-uppercase text-secondary text-xs font-weight-bolder">Tipster</th>
                  <th class="text-uppercase text-secondary text-xs font-weight-bolder">Moneda</th>
                  <th class="text-uppercase text-secondary text-xs font-weight-bolder text-end">Nº ventas</th>
                  <th class="text-uppercase text-secondary text-xs font-weight-bolder text-end">Total ventas</th>
                  <th class="text-uppercase text-secondary text-xs font-weight-bolder text-end">Comisión %</th>
                  <th class="text-uppercase text-secondary text-xs font-weight-bolder text-end">Comisión</th>
                </tr>
              </thead>
              <tbody>
                <template v-for="fila in datos" :key="filaKey(fila)">
                  <!-- Fila principal -->
                  <tr>
                    <td class="text-sm font-weight-bold">{{ fila.tipster }}</td>
                    <td class="text-sm">
                      <span :class="['badge-moneda', fila.moneda === 'USD' ? 'badge-usd' : 'badge-eur']">
                        {{ fila.moneda }}
                      </span>
                    </td>
                    <td class="text-end text-sm">
                      <span
                        class="ventas-toggle"
                        :title="expandidos.has(filaKey(fila)) ? 'Ocultar ventas' : 'Ver ventas'"
                        @click="toggleDetalle(fila)"
                      >
                        {{ fila.num_ventas }}
                        <span v-if="cargandoDetalle[fila.tipster_id]" class="spinner-border spinner-border-sm ms-1 align-middle"></span>
                        <span v-else class="toggle-chevron">{{ expandidos.has(filaKey(fila)) ? '▲' : '▼' }}</span>
                      </span>
                    </td>
                    <td class="text-end text-sm font-weight-bold">{{ formatMonto(fila.total_ventas, fila.moneda) }}</td>
                    <td class="text-end text-sm">{{ formatPct(fila.comision_pct) }}</td>
                    <td class="text-end text-sm font-weight-bold comision-eur">{{ formatMonto(fila.comision_eur, fila.moneda) }}</td>
                  </tr>

                  <!-- Fila de detalle expandida -->
                  <tr v-if="expandidos.has(filaKey(fila))" class="fila-detalle">
                    <td colspan="6" class="p-0">
                      <div class="detalle-container">
                        <div v-if="cargandoDetalle[fila.tipster_id]" class="text-center py-3">
                          <span class="spinner-border spinner-border-sm text-primary"></span>
                        </div>
                        <template v-else-if="detalles[fila.tipster_id]?.length">
                          <table class="tabla-ventas w-100">
                            <thead>
                              <tr>
                                <th>Referencia</th>
                                <th>Fecha</th>
                                <th>Servicio</th>
                                <th class="text-end">Precio</th>
                                <th v-if="tieneDevolucion(fila.tipster_id)" class="text-end">Devuelto</th>
                                <th class="text-end">Neto</th>
                                <th>Moneda</th>
                              </tr>
                            </thead>
                            <tbody>
                              <tr v-for="venta in detalles[fila.tipster_id]"
                                  :key="venta.referencia + venta.fecha"
                                  :class="{ 'venta-devuelta': venta.totalmente_devuelta }">
                                <td>
                                  {{ venta.referencia }}
                                  <span v-if="venta.totalmente_devuelta" class="badge-devuelta">Devuelta</span>
                                  <span v-if="venta.tipo_pago === 'suscripcion'" class="badge-renov" title="Cobro de suscripción">↻</span>
                                </td>
                                <td>{{ formatFecha(venta.fecha) }}</td>
                                <td>{{ venta.servicio }}</td>
                                <td class="text-end">{{ formatMonto(venta.precio, venta.moneda) }}</td>
                                <td v-if="tieneDevolucion(fila.tipster_id)" class="text-end devuelto-col">
                                  {{ venta.devuelto > 0 ? formatMonto(venta.devuelto, venta.moneda) : '—' }}
                                </td>
                                <td class="text-end" :class="{ 'text-muted': venta.totalmente_devuelta }">
                                  {{ venta.totalmente_devuelta ? '—' : formatMonto(venta.neto, venta.moneda) }}
                                </td>
                                <td>
                                  <span :class="['badge-moneda', venta.moneda === 'USD' ? 'badge-usd' : 'badge-eur']">
                                    {{ venta.moneda || 'EUR' }}
                                  </span>
                                </td>
                              </tr>
                            </tbody>
                            <tfoot>
                              <tr class="detalle-subtotal">
                                <td :colspan="tieneDevolucion(fila.tipster_id) ? 4 : 3" class="text-end">
                                  Subtotal efectivo
                                </td>
                                <td v-if="tieneDevolucion(fila.tipster_id)" class="text-end devuelto-col">
                                  {{ formatMonto(totalDevueltoTipster(fila.tipster_id), fila.moneda) }}
                                </td>
                                <td class="text-end font-weight-bold">
                                  {{ formatMonto(fila.total_ventas, fila.moneda) }}
                                </td>
                                <td></td>
                              </tr>
                            </tfoot>
                          </table>
                          <p v-if="tieneDevolucionTotal(fila.tipster_id)" class="detalle-nota">
                            * Las ventas marcadas como "Devuelta" no computan en el subtotal ni en la comisión.
                          </p>
                        </template>
                        <p v-else class="text-center py-3 mb-0 text-muted text-sm">Sin ventas en este período.</p>
                      </div>
                    </td>
                  </tr>
                </template>

                <!-- Fila(s) de totales por moneda -->
                <template v-for="(total, moneda) in totalesPorMoneda" :key="'total_' + moneda">
                  <tr class="fila-total">
                    <td class="font-weight-bold">{{ Object.keys(totalesPorMoneda).length > 1 ? 'TOTAL ' + moneda : 'TOTAL' }}</td>
                    <td>
                      <span :class="['badge-moneda', moneda === 'USD' ? 'badge-usd' : 'badge-eur']">{{ moneda }}</span>
                    </td>
                    <td class="text-end font-weight-bold">{{ total.num_ventas }}</td>
                    <td class="text-end font-weight-bold">{{ formatMonto(total.importe, moneda) }}</td>
                    <td></td>
                    <td class="text-end font-weight-bold comision-eur">{{ formatMonto(total.comision, moneda) }}</td>
                  </tr>
                </template>
              </tbody>
            </table>
          </div>
        </div>

      </div>
    </div>
  </div>
</template>

<script>
import showSwal from "@/mixins/showSwal.js";

export default {
  name: "Comisiones",
  data() {
    const ahora = new Date();
    return {
      periodo:    'mes',
      mes:        ahora.getMonth() + 1,
      trimestre:  Math.ceil((ahora.getMonth() + 1) / 3),
      semestre:   ahora.getMonth() < 6 ? 1 : 2,
      anio:       ahora.getFullYear(),
      tipsterId:  '',

      datos:           [],
      periodoLabel:    '',
      detalles:        {},
      cargandoDetalle: {},
      expandidos:      new Set(),

      listaTipsters: [],
      loading:       false,
      exportando:    false,
      generandoPdf:  false,
      generado:      false,

      periodos: [
        { v: 'mes',       l: 'Mes' },
        { v: 'trimestre', l: 'Trimestre' },
        { v: 'semestre',  l: 'Semestre' },
        { v: 'anio',      l: 'Año' },
      ],
      meses: [
        { v: 1,  l: 'Enero' },   { v: 2,  l: 'Febrero' },  { v: 3,  l: 'Marzo' },
        { v: 4,  l: 'Abril' },   { v: 5,  l: 'Mayo' },     { v: 6,  l: 'Junio' },
        { v: 7,  l: 'Julio' },   { v: 8,  l: 'Agosto' },   { v: 9,  l: 'Septiembre' },
        { v: 10, l: 'Octubre' }, { v: 11, l: 'Noviembre' }, { v: 12, l: 'Diciembre' },
      ],
    };
  },
  computed: {
    anios() {
      const hoy = new Date().getFullYear();
      const lista = [];
      for (let y = hoy; y >= hoy - 4; y--) lista.push(y);
      return lista;
    },
    totalesPorMoneda() {
      const result = {};
      for (const f of this.datos) {
        const m = f.moneda || 'EUR';
        if (!result[m]) result[m] = { num_ventas: 0, importe: 0, comision: 0 };
        result[m].num_ventas += f.num_ventas;
        result[m].importe   += f.total_ventas;
        result[m].comision  += f.comision_eur;
      }
      return result;
    },
  },
  async mounted() {
    try {
      const resp = await this.$store.dispatch("auth/listaTipsters");
      this.listaTipsters = Array.isArray(resp) ? resp : [];
    } catch (e) { /* silencioso */ }
    // Pre-rellenar tipster desde el hub de Informes
    if (this.$route.query.tipster_id) {
      this.tipsterId = parseInt(this.$route.query.tipster_id);
    }
  },
  methods: {
    filaKey(fila) {
      return fila.tipster_id + '_' + (fila.moneda || 'EUR');
    },
    formatMonto(v, moneda) {
      const currency = moneda === 'USD' ? 'USD' : 'EUR';
      return Number(v).toLocaleString('es-ES', { style: 'currency', currency });
    },
    formatPct(v) {
      return Number(v).toLocaleString('es-ES', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' %';
    },
    formatFecha(f) {
      if (!f) return '';
      return new Date(f).toLocaleDateString('es-ES', { day: '2-digit', month: '2-digit', year: 'numeric' });
    },
    payload() {
      return {
        periodo:   this.periodo,
        mes:       this.mes,
        trimestre: this.trimestre,
        semestre:  this.semestre,
        anio:      this.anio,
        tipster_id: this.tipsterId || 0,
      };
    },

    async cargar() {
      this.loading = true;
      this.expandidos = new Set();
      this.detalles   = {};
      try {
        const resp = await this.$store.dispatch("auth/informeComisiones", this.payload());
        if (resp?.res === 'OK') {
          this.datos        = resp.datos || [];
          this.periodoLabel = resp.label  || '';
          this.generado     = true;
        } else {
          showSwal.methods.showSwal({ type: 'error', message: resp?.mensaje || 'Error al generar informe', width: 500 });
        }
      } catch (e) {
        showSwal.methods.showSwal({ type: 'error', message: 'Error de conexión', width: 500 });
      } finally {
        this.loading = false;
      }
    },

    async toggleDetalle(fila) {
      const key = this.filaKey(fila);
      const set = new Set(this.expandidos);
      if (set.has(key)) {
        set.delete(key);
        this.expandidos = set;
        return;
      }
      set.add(key);
      this.expandidos = set;

      if (!this.detalles[fila.tipster_id]) {
        await this.cargarDetalle(fila.tipster_id);
      }
    },

    async cargarDetalle(tipsterId) {
      this.cargandoDetalle = { ...this.cargandoDetalle, [tipsterId]: true };
      try {
        const resp = await this.$store.dispatch("auth/detalleComisiones", {
          ...this.payload(),
          tipster_id: tipsterId,
        });
        if (resp?.res === 'OK') {
          this.detalles = { ...this.detalles, [tipsterId]: resp.ventas || [] };
        }
      } catch (e) {
        this.detalles = { ...this.detalles, [tipsterId]: [] };
      } finally {
        const cargando = { ...this.cargandoDetalle };
        delete cargando[tipsterId];
        this.cargandoDetalle = cargando;
      }
    },

    async exportar() {
      this.exportando = true;
      try {
        const resp = await this.$store.dispatch("auth/exportarComisiones", this.payload());
        const url  = window.URL.createObjectURL(new Blob([resp.data]));
        const link = document.createElement('a');
        link.href  = url;
        link.setAttribute('download', `comisiones_${this.periodoLabel.replace(/[^a-z0-9]/gi, '_')}.xlsx`);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
      } catch (e) {
        showSwal.methods.showSwal({ type: 'error', message: 'Error al exportar', width: 500 });
      } finally {
        this.exportando = false;
      }
    },

    async generarPdf() {
      this.generandoPdf = true;
      try {
        for (const fila of this.datos) {
          if (!this.detalles[fila.tipster_id]) {
            await this.cargarDetalle(fila.tipster_id);
          }
        }

        const html = this.buildPdfHtml();
        const win  = window.open('', '_blank');
        if (!win) {
          showSwal.methods.showSwal({
            type: 'error',
            message: 'Permite ventanas emergentes en el navegador para generar el PDF.',
            width: 500,
          });
          return;
        }
        win.document.write(html);
        win.document.close();
        win.focus();
        setTimeout(() => { win.print(); }, 700);
      } catch (e) {
        showSwal.methods.showSwal({ type: 'error', message: 'Error al generar PDF', width: 500 });
      } finally {
        this.generandoPdf = false;
      }
    },

    tieneDevolucion(tipsterId) {
      return (this.detalles[tipsterId] || []).some(v => v.devuelto > 0);
    },
    tieneDevolucionTotal(tipsterId) {
      return (this.detalles[tipsterId] || []).some(v => v.totalmente_devuelta);
    },
    totalDevueltoTipster(tipsterId) {
      return (this.detalles[tipsterId] || []).reduce((s, v) => s + (v.devuelto || 0), 0);
    },

    buildPdfHtml() {
      const logoUrl = window.location.origin + '/img/quanty.png';
      const fechaGen = new Date().toLocaleDateString('es-ES', {
        day: '2-digit', month: 'long', year: 'numeric',
      });

      let cuerpoTabla = '';
      for (const fila of this.datos) {
        const ventas = this.detalles[fila.tipster_id] || [];
        const hayDev = ventas.some(v => v.devuelto > 0);

        cuerpoTabla += `
          <tr>
            <td><strong>${fila.tipster}</strong></td>
            <td>${fila.moneda || 'EUR'}</td>
            <td class="r">${fila.num_ventas}</td>
            <td class="r"><strong>${this.formatMonto(fila.total_ventas, fila.moneda)}</strong></td>
            <td class="r">${this.formatPct(fila.comision_pct)}</td>
            <td class="r"><strong>${this.formatMonto(fila.comision_eur, fila.moneda)}</strong></td>
          </tr>`;

        if (ventas.length) {
          const colDevuelto = hayDev ? '<th class="r">Devuelto</th>' : '';
          const filasVentas = ventas.map(v => {
            const devCol = hayDev
              ? `<td class="r ${v.totalmente_devuelta ? 'dev-amt' : ''}">${v.devuelto > 0 ? this.formatMonto(v.devuelto, v.moneda) : '—'}</td>`
              : '';
            const netoTxt = v.totalmente_devuelta ? '—' : this.formatMonto(v.neto, v.moneda);
            const badge = v.totalmente_devuelta ? ' <span class="badge-dev">Devuelta</span>' : '';
            const renovBadge = v.tipo_pago === 'suscripcion' ? ' <span class="badge-renov-pdf">↻</span>' : '';
            const rowClass = v.totalmente_devuelta ? ' class="venta-dev"' : '';
            return `<tr${rowClass}><td>${v.referencia}${badge}${renovBadge}</td><td>${this.formatFecha(v.fecha)}</td><td>${v.servicio}</td><td class="r">${this.formatMonto(v.precio, v.moneda)}</td>${devCol}<td class="r">${netoTxt}</td></tr>`;
          }).join('');

          const subCols = hayDev ? 5 : 4;
          cuerpoTabla += `
          <tr class="sub-row">
            <td colspan="6" style="padding:0 0 10px 32px">
              <table class="sub-tabla">
                <thead><tr><th>Referencia</th><th>Fecha</th><th>Servicio</th><th class="r">Precio</th>${colDevuelto}<th class="r">Neto</th></tr></thead>
                <tbody>${filasVentas}</tbody>
                <tfoot><tr class="sub-total"><td colspan="${subCols - 1}" class="r">Subtotal efectivo</td>${hayDev ? `<td class="r dev-amt">${this.formatMonto(this.totalDevueltoTipster(fila.tipster_id), fila.moneda)}</td>` : ''}<td class="r"><strong>${this.formatMonto(fila.total_ventas, fila.moneda)}</strong></td></tr></tfoot>
              </table>
            </td>
          </tr>`;
        }
      }

      // Totales por moneda
      for (const [moneda, total] of Object.entries(this.totalesPorMoneda)) {
        const label = Object.keys(this.totalesPorMoneda).length > 1 ? `TOTAL ${moneda}` : 'TOTAL';
        cuerpoTabla += `
        <tr class="total-row">
          <td>${label}</td>
          <td>${moneda}</td>
          <td class="r">${total.num_ventas}</td>
          <td class="r">${this.formatMonto(total.importe, moneda)}</td>
          <td></td>
          <td class="r">${this.formatMonto(total.comision, moneda)}</td>
        </tr>`;
      }

      return `<!DOCTYPE html>
<html lang="es">
<head>
  <meta charset="UTF-8">
  <title>Informe de Ventas de ${this.periodoLabel}</title>
  <style>
    *{box-sizing:border-box;margin:0;padding:0}
    body{font-family:Arial,sans-serif;font-size:12px;color:#333;padding:30px}
    img.logo{max-height:55px;margin-bottom:18px}
    h1{font-size:17px;font-weight:bold;color:#1a1a2e;margin-bottom:4px}
    .meta{font-size:11px;color:#777;margin-bottom:22px}
    table{width:100%;border-collapse:collapse;margin-bottom:12px}
    th{background:#1e88e5;color:#fff;padding:7px 10px;text-align:left;font-size:11px;text-transform:uppercase}
    td{padding:6px 10px;border-bottom:1px solid #e8e8e8;font-size:12px}
    .r{text-align:right}
    tr:nth-child(even) td{background:#f8f9fa}
    .sub-row td{background:transparent!important;border-bottom:none}
    .sub-tabla{border:1px solid #ddd;margin-top:0}
    .sub-tabla th{background:#ecf3fc;color:#444;font-size:10px}
    .sub-tabla td{font-size:11px;padding:5px 8px}
    .venta-dev td{color:#bbb;text-decoration:line-through}
    .badge-dev{display:inline-block;padding:0 5px;border-radius:8px;font-size:9px;font-weight:700;background:#fee2e2;color:#b91c1c;text-decoration:none;vertical-align:middle}
    .badge-renov-pdf{color:#7c3aed;font-size:10px;margin-left:3px}
    .dev-amt{color:#ef4444}
    .sub-total td{background:#f0f7ff!important;font-weight:600;border-top:1px solid #bfdbfe}
    .total-row td{background:#e3f2fd!important;font-weight:bold;border-top:2px solid #1e88e5}
    @page{margin:2cm}
    @media print{body{padding:0}}
  </style>
</head>
<body>
  <img src="${logoUrl}" class="logo" alt="Quanty">
  <h1>Informe de Ventas de ${this.periodoLabel}</h1>
  <p class="meta">Generado el ${fechaGen}</p>
  <table>
    <thead>
      <tr>
        <th>Tipster</th>
        <th>Moneda</th>
        <th class="r">Nº ventas</th>
        <th class="r">Total ventas</th>
        <th class="r">Comisión %</th>
        <th class="r">Comisión</th>
      </tr>
    </thead>
    <tbody>${cuerpoTabla}</tbody>
  </table>
</body>
</html>`;
    },
  },
};
</script>

<style scoped>
.breadcrumb-item { cursor: pointer; color: var(--q-gold); }
.breadcrumb-item:hover { text-decoration: underline; }

/* Selector de período */
.periodo-tabs {
  display: flex;
  gap: 6px;
  flex-wrap: wrap;
}
.periodo-tab {
  padding: 5px 16px;
  border: 1.5px solid var(--q-border);
  border-radius: 20px;
  background: var(--q-card);
  font-size: 0.82rem;
  font-weight: 600;
  color: var(--q-muted);
  cursor: pointer;
  transition: all 0.18s;
}
.periodo-tab:hover { border-color: var(--q-gold); color: var(--q-gold); }
.periodo-tab.active {
  background: var(--q-gold);
  border-color: var(--q-gold);
  color: var(--q-gold-on);
}

/* Filtros */
.filtros-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(170px, 1fr));
  gap: 12px 16px;
  max-width: 720px;
}
.filtro-grupo { display: flex; flex-direction: column; gap: 4px; }
.filtro-label {
  font-size: 0.72rem;
  font-weight: 600;
  color: var(--q-muted);
  text-transform: uppercase;
  letter-spacing: 0.04em;
}
.filtro-input {
  padding: 7px 10px;
  border: 1.5px solid var(--q-border);
  border-radius: 7px;
  font-size: 0.85rem;
  color: var(--q-text);
  background: var(--q-card-alt);
  outline: none;
  width: 100%;
  transition: border-color 0.2s;
}
.filtro-input:focus { border-color: var(--q-gold); }

/* Acciones */
.acciones-row {
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
}

@media (max-width: 480px) {
  .acciones-row {
    flex-direction: column;
  }
  .acciones-row .btn {
    width: 100%;
    text-align: center;
  }
}

/* Etiqueta del período activo */
.periodo-label-txt {
  font-weight: 600;
  color: var(--q-gold);
}

/* Badge de moneda */
.badge-moneda {
  display: inline-block;
  padding: 1px 7px;
  border-radius: 10px;
  font-size: 0.65rem;
  font-weight: 700;
  letter-spacing: 0.04em;
}
.badge-eur {
  background: rgba(30, 136, 229, 0.12);
  color: #1e88e5;
}
.badge-usd {
  background: rgba(34, 197, 94, 0.12);
  color: #16a34a;
}

/* Ventas clickable */
.ventas-toggle {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  cursor: pointer;
  color: var(--q-gold);
  font-weight: 600;
  white-space: nowrap;
  padding: 2px 6px;
  border-radius: 4px;
  transition: background 0.15s;
}
.ventas-toggle:hover { background: var(--q-hover); }
.toggle-chevron { font-size: 0.7rem; opacity: 0.7; }

/* Fila de detalle */
.fila-detalle { background: transparent !important; }
.detalle-container {
  margin: 0 0 8px 28px;
  border-left: 3px solid var(--q-gold);
  padding-left: 0;
  overflow-x: auto;
}
.tabla-ventas { border-collapse: collapse; font-size: 0.82rem; min-width: 360px; }

@media (max-width: 480px) {
  .detalle-container { margin-left: 8px; }
}
.tabla-ventas th {
  background: var(--q-hover);
  color: var(--q-text);
  padding: 5px 10px;
  font-size: 0.72rem;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  font-weight: 700;
}
.tabla-ventas td { padding: 5px 10px; border-bottom: 1px solid var(--q-border); color: var(--q-muted); }
.tabla-ventas tr:last-child td { border-bottom: none; }

/* Badge renovación */
.badge-renov {
  display: inline-block;
  margin-left: 4px;
  font-size: 0.7rem;
  color: var(--q-gold);
  vertical-align: middle;
}

/* Total */
.fila-total { background: var(--q-hover); }
.comision-eur { color: var(--q-gold); }

/* Ventas devueltas en el detalle */
.venta-devuelta td {
  color: var(--q-muted);
  background: var(--q-card-alt) !important;
  text-decoration: line-through;
  text-decoration-color: var(--q-border-md);
}
.badge-devuelta {
  display: inline-block;
  margin-left: 6px;
  padding: 1px 7px;
  border-radius: 10px;
  font-size: 0.65rem;
  font-weight: 700;
  background: var(--q-red-soft);
  color: var(--q-red);
  text-decoration: none;
  vertical-align: middle;
  line-height: 1.6;
}
.devuelto-col { color: var(--q-red); }

/* Subtotal del detalle */
.detalle-subtotal td {
  background: var(--q-hover) !important;
  font-size: 0.8rem;
  font-weight: 600;
  color: var(--q-text);
  border-top: 1.5px solid var(--q-border-md);
  padding: 6px 10px;
}

/* Nota de devoluciones */
.detalle-nota {
  font-size: 0.72rem;
  color: var(--q-muted);
  margin: 4px 0 8px 10px;
  font-style: italic;
}

@media (max-width: 480px) {
  .tabla-ventas { min-width: 480px; }
}
</style>
